'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      category: 'Quotes',
      question: 'How quickly will I receive a quote?',
      answer: 'Our team responds to all quote requests within 2 hours during business hours. For urgent requests, you can call our 24/7 emergency hotline for immediate assistance.'
    },
    {
      category: 'Quotes',
      question: 'What information do I need to provide for an accurate quote?',
      answer: 'We need details about your cargo type, weight, value, origin and destination countries, and preferred timeline. Additional information about special handling requirements helps us provide more accurate pricing.'
    },
    {
      category: 'Services',
      question: 'Do you handle cross-border shipments?',
      answer: 'Yes, we specialize in cross-border logistics across East, Central, and Southern Africa. Our customs brokerage services ensure smooth clearance at all border points.'
    },
    {
      category: 'Services',
      question: 'What types of cargo do you transport?',
      answer: 'We transport precious minerals, heavy mining equipment, general cargo, and provide secure personnel movement. Our fleet is equipped to handle specialized cargo with proper safety protocols.'
    },
    {
      category: 'Tracking',
      question: 'Can I track my shipment in real-time?',
      answer: 'Yes, all clients receive access to our tracking portal with GPS-enabled real-time monitoring. You can view your shipment location, estimated arrival time, and delivery status 24/7.'
    },
    {
      category: 'Safety',
      question: 'What safety measures do you have in place?',
      answer: 'We maintain comprehensive safety protocols including GPS tracking, armed escorts for high-value cargo, regular vehicle maintenance, driver training programs, and full insurance coverage for all shipments.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-4 animate-pulse">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-muted rounded-lg h-20"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-golden-light/20 to-golden-dark/20 px-4 py-2 rounded-full mb-4">
            <Icon name="QuestionMarkCircleIcon" size={20} className="text-golden-mid" />
            <span className="font-body text-sm font-semibold text-primary">FAQ</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Find answers to common questions about our services and processes
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-card rounded-xl shadow-elevation-1 overflow-hidden transition-all duration-300 hover:shadow-elevation-2"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors duration-300 hover:bg-muted/50"
              >
                <div className="flex-1 pr-4">
                  <span className="inline-block px-3 py-1 bg-gradient-to-r from-golden-light/20 to-golden-dark/20 text-golden-mid font-body text-xs font-semibold rounded-full mb-2">
                    {faq.category}
                  </span>
                  <h3 className="font-headline font-semibold text-lg text-foreground">
                    {faq.question}
                  </h3>
                </div>
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                  openIndex === index
                    ? 'bg-gradient-to-br from-golden-light to-golden-dark rotate-180' :'bg-muted'
                }`}>
                  <Icon 
                    name="ChevronDownIcon" 
                    size={20} 
                    className={openIndex === index ? 'text-primary' : 'text-muted-foreground'}
                  />
                </div>
              </button>
              
              <div
                className={`transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                } overflow-hidden`}
              >
                <div className="px-6 pb-5 pt-2">
                  <p className="font-body text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="font-body text-muted-foreground mb-4">
            Still have questions? Our team is here to help.
          </p>
          <a
            href="mailto:mylesdrivelc@gmail.com"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark text-primary font-cta font-semibold rounded-lg shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-0.5"
          >
            <Icon name="EnvelopeIcon" size={20} />
            <span>Contact Support</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;